import React from "react";
import ReactDOM from "react-dom/client";
import { I18nProvider } from "@lingui/react";
import ErrorBoundary from "./components/ErrorBoundary";
import { applyCachedThemeMode } from "./lib/theme";
import { setInferenceFetch } from "@aicc/inference";
import { REMOTE_UNAUTHORIZED, isRemote, relayFetch } from "./lib/rpc";
import { installImeGuard } from "./lib/imeGuard";
import { isLinuxDesktop } from "./lib/platform";
import { providerFetch } from "./lib/nativeFetch";
import { activateUiLocale, i18n } from "./lib/i18n";

applyCachedThemeMode(); // set light/dark before first paint (real theme loads per-account after unlock)

// Keystrokes an input method is using (Chinese, Japanese, Korean) never reach the app's key handlers — see
// imeGuard.ts. Before anything else listens for keys; pop-out windows and the phone remote load this entry too.
installImeGuard();

// Save what is still being typed before this window closes (BUG-075, closeguard.rs). Every desktop window
// loads this entry, pop-outs included, and each one answers for itself.
if (!isRemote()) void import("./lib/appClose").then((m) => m.installCloseGuard()).catch(() => {});

// F6 is the webview's own "move focus to the next pane" key. The desktop window has no other pane, so
// focus left the page and the window went black until Alt+F4. Desktop only: in a phone/laptop browser
// F6 is the address bar, which people use.
if (!isRemote()) {
  window.addEventListener("keydown", (e) => { if (e.key === "F6") e.preventDefault(); }, { capture: true });
}

// Ctrl+Z on Linux. WebKitGTK keeps an undo history for every text field but binds no key to it (its key
// table has select-all, bold and the clipboard, not undo): a browser built on it adds its own shortcut,
// so in this app Ctrl+Z did nothing anywhere. Measured on WebKitGTK 2.52: the key reaches the page,
// nothing undoes, and execCommand("undo") does. WebView2 undoes by itself, so Linux only. Bubble phase
// and `defaultPrevented`, so a field with its own Ctrl+Z still wins. The letter typed, as a browser reads
// a shortcut (German keyboards swap Z and Y), falling back to the key's position only when the layout
// types no Latin letter there: a Cyrillic Z key reads "я".
if (isLinuxDesktop) {
  window.addEventListener("keydown", (e) => {
    if (e.defaultPrevented || e.isComposing || !e.ctrlKey || e.altKey || e.metaKey) return;
    const letter = /^[a-z]$/i.test(e.key) ? e.key.toLowerCase() : e.code === "KeyZ" ? "z" : e.code === "KeyY" ? "y" : "";
    const undo = letter === "z" && !e.shiftKey;
    const redo = (letter === "z" && e.shiftKey) || (letter === "y" && !e.shiftKey);
    const el = e.target;
    if (!(undo || redo) || !(el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement || (el instanceof HTMLElement && el.isContentEditable))) return;
    e.preventDefault();
    document.execCommand(undo ? "undo" : "redo");
  });
}

// A remote client has no API keys — the desktop refuses to hand credentials to a browser — so its
// provider requests go through the desktop, which attaches the key and streams the reply straight back.
// Installed before the app mounts so no generation can start on the direct transport by accident.
if (isRemote()) {
  setInferenceFetch(async (url, init) => {
    const res = await relayFetch(url, {
      headers: (init.headers ?? {}) as Record<string, string>,
      body: typeof init.body === "string" ? init.body : "",
      signal: init.signal ?? null,
    });
    // relayFetch owns the envelope, not the PIN gate: a 401 here (idle-expired PIN window) used to
    // surface as "check the provider's API key", advice a phone can't even follow.
    if (res.status === 401) window.dispatchEvent(new CustomEvent(REMOTE_UNAUTHORIZED));
    return res;
  });
} else {
  // Direct, or sent natively for an endpoint whose server blocks the webview (no CORS headers): lib/nativeFetch.ts.
  setInferenceFetch(providerFetch);
}

// The UI language must be active before the app's modules even load, not just before the first render:
// App is imported only after activation, so a t`…` in a module-level constant (Settings' leaf list, option
// tables) reads the chosen language. The language never changes mid-run (a change applies on reload), so
// reading it once at load is correct. Modules THIS file imports statically — and what they import — load
// before the language: lib/theme, lib/rpc, lib/imeGuard, lib/i18n, ErrorBoundary, lib/api, lib/accounts,
// lib/bgImage, lib/debugLog. In those, a module-level string is a lazy msg`…` descriptor, translated where
// it's shown with i18n._(…); t`…` is fine inside their functions.
// activateUiLocale always ends with a language active (English at worst), so the app never renders blank.
void activateUiLocale().finally(async () => {
  const { default: App } = await import("./App");
  ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
      <I18nProvider i18n={i18n}>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </I18nProvider>
    </React.StrictMode>,
  );
});
